import { Injectable } from '@angular/core';
import {
	AlarisLanguageService,
	clamp,
	Day,
	MILLISECONDS_IN_HOUR,
	pluralize,
	Russian,
} from '@campaign-portal/components-library';
import {
	CampaignRepeatMonthlyMode,
	CampaignRepeatRule,
	CampaignRepeatType,
} from '@campaign-portal/namespace/entities/campaigns/specs';
import { DAYS_IN_WEEK } from '@shared/constants';
import { ContactFieldsService } from '@shared/services';
import { InputComplexType } from '@campaign-portal/namespace/common/entityField';
import { Weekday } from '@campaign-portal/namespace/common/enums';

const DAY_POSTFIX_THRESHOLD = 3;
const WEEK_POSTFIX_THRESHOLD = 3;
const MAX_DAY_OF_WEEK = 6;

export enum CampaignNextRunDay {
	EMPTY = 'EMPTY',
	TODAY = 'TODAY',
	TOMORROW = 'TOMORROW',
	OTHER = 'OTHER',
}

const enum DayOfWeek {
	Sunday,
	Monday,
	Tuesday,
	Wednesday,
	Thursday,
	Friday,
	Saturday,
}

@Injectable({
	providedIn: 'root',
})
export class CampaignSchedulerUtilsService {
	constructor(
		private readonly contactFieldsService: ContactFieldsService,
		private readonly ls: AlarisLanguageService
	) {}

	isCurrentlyRepeating(repeatRule: CampaignRepeatRule): boolean {
		const { repeatType, endDate } = repeatRule;
		return repeatType !== CampaignRepeatType.NONE && (endDate ? new Date(endDate) > new Date() : true);
	}

	getNextRunDay(nextRunAt?: string): CampaignNextRunDay {
		if (!nextRunAt) {
			return CampaignNextRunDay.EMPTY;
		}
		const nextRunDay = Day.fromLocalNativeDate(new Date(nextRunAt));
		const today = Day.currentLocal();
		if (today.daySame(nextRunDay)) {
			return CampaignNextRunDay.TODAY;
		}
		const tomorrow = today.append({ day: 1 });
		if (tomorrow.daySame(nextRunDay)) {
			return CampaignNextRunDay.TOMORROW;
		}
		return CampaignNextRunDay.OTHER;
	}

	displayRepeatType = (type: CampaignRepeatType): string => {
		return this.ls.translate('campaigns.scheduler.repeatType.' + type);
	};

	displayIntervalType(type: CampaignRepeatType, interval?: number | null): string {
		const createPluralizeWords = (prefix: string): string[] => {
			return [prefix + '.one', prefix + '.other', prefix + '.few'];
		};

		let postfix = '';
		switch (type) {
			case CampaignRepeatType.DAILY:
				postfix = pluralize(interval ?? 0, createPluralizeWords('campaigns.scheduler.days'));
				break;
			case CampaignRepeatType.WEEKLY:
				postfix = pluralize(interval ?? 0, createPluralizeWords('campaigns.scheduler.weeks'));
				break;
			case CampaignRepeatType.MONTHLY:
				postfix = pluralize(interval ?? 0, createPluralizeWords('campaigns.scheduler.months'));
				break;
			case CampaignRepeatType.YEARLY:
				postfix = pluralize(interval ?? 0, createPluralizeWords('campaigns.scheduler.years'));
				break;
		}
		return postfix ? this.ls.translate(postfix) : postfix;
	}

	displayRepeatContactVariable = (variable: string): string => {
		const contactDateFields = this.contactFieldsService.list.filter((field) => field.type === InputComplexType.DATE);
		return contactDateFields.find((field) => field.variable === variable)?.name ?? '';
	};

	displayWeekdayOfMonth(day: Day): string {
		const weeks = Math.ceil(day.day / DAYS_IN_WEEK);
		const weeksPostfix = this.displayWeeksPostfix(weeks, day.dayOfWeek(false));
		const dayOfWeek = this.displayDayOfWeek(day.dayOfWeek(false));
		return `${weeks}${weeksPostfix} ${dayOfWeek}`;
	}

	displayDayOfMonth(day: number): string {
		const prefix = 'campaigns.scheduler.dayOfMonthPostfix.';
		const dayPostfix =
			day <= DAY_POSTFIX_THRESHOLD ? this.ls.translate(prefix + day) : this.ls.translate(prefix + 'other');
		return `${day}${dayPostfix}`;
	}

	displayRepeatRule(rule: CampaignRepeatRule): string {
		const type = this.displayRepeatType(rule.repeatType);
		const separator = ', ';

		switch (rule.repeatType) {
			case CampaignRepeatType.NONE:
				return type;
			case CampaignRepeatType.CONTACT_DATE: {
				const dateFieldName = this.displayRepeatContactVariable(rule.repeatContactVariable ?? '');
				const name = dateFieldName ? this.ls.translate(dateFieldName) : '';
				return [type, name].filter(Boolean).join(separator);
			}
			case CampaignRepeatType.DAILY:
			case CampaignRepeatType.WEEKLY:
			case CampaignRepeatType.MONTHLY:
			case CampaignRepeatType.YEARLY:
				return this.displayIntervalBasedRepeatRule(rule, type, separator);
		}
	}

	private displayIntervalBasedRepeatRule(rule: CampaignRepeatRule, type: string, separator: string): string {
		const intervalType = this.displayIntervalType(rule.repeatType, rule.repeatInterval);
		const every = this.ls.translate('fc.every').toLowerCase();
		const interval = `${every} ${rule.repeatInterval ?? 0} ${intervalType}`;

		if (rule.repeatType === CampaignRepeatType.WEEKLY && rule.repeatWeekdays?.length) {
			const weekdays = this.displayRepeatRuleWeekdays(rule.repeatWeekdays);
			return [type, interval, weekdays].join(separator);
		}
		if (rule.repeatType === CampaignRepeatType.MONTHLY) {
			const monthMode = this.displayRepeatRuleMonthMode(rule);
			return [type, interval, monthMode].join(separator);
		}
		return [type, interval].join(separator);
	}

	private displayRepeatRuleWeekdays(weekdays: Weekday[]): string {
		const on = this.ls.translate('campaigns.scheduler.on').toLowerCase();
		const days = weekdays
			.map((weekday) => {
				const titlecase = weekday[0].toUpperCase() + weekday.slice(1).toLowerCase();
				return this.ls.translate('calendar.days.' + titlecase);
			})
			.join(', ');
		return `${on} ${days}`;
	}

	private displayRepeatRuleMonthMode(rule: CampaignRepeatRule): string {
		const { startDate, userTimezone, repeatMonthlyMode } = rule;
		if (!repeatMonthlyMode) {
			return '';
		}
		const timestamp = new Date(startDate).getTime() + userTimezone * MILLISECONDS_IN_HOUR;
		const startDay = Day.fromUtcNativeDate(new Date(timestamp));
		switch (repeatMonthlyMode) {
			case CampaignRepeatMonthlyMode.BY_DAY_OF_MONTH: {
				const on = this.ls.translate('campaigns.scheduler.on').toLowerCase();
				const day = this.displayDayOfMonth(startDay.day);
				const dayOfMonth = this.ls.translate('campaigns.scheduler.dayOfMonth');
				return `${on} ${day} ${dayOfMonth}`;
			}
			case CampaignRepeatMonthlyMode.BY_WEEKDAY: {
				return this.displayWeekdayOfMonth(startDay);
			}
		}
	}

	private displayWeeksPostfix(weeks: number, dayOfWeek: number): string {
		if (this.ls.current().id === Russian.id) {
			const prefix = 'campaigns.scheduler.weekdayOfMonthGenderPostfix.';
			const feminine = [DayOfWeek.Wednesday, DayOfWeek.Friday, DayOfWeek.Saturday];
			const neuter = [DayOfWeek.Sunday];

			let key: string;
			if (feminine.includes(dayOfWeek)) {
				key = prefix + 'feminine';
			} else if (neuter.includes(dayOfWeek)) {
				key = prefix + 'neuter';
			} else {
				key = prefix + 'masculine';
			}

			return this.ls.translate(key);
		}
		const prefix = 'campaigns.scheduler.weekdayOfMonthPostfix.';
		return weeks <= WEEK_POSTFIX_THRESHOLD ? this.ls.translate(prefix + weeks) : this.ls.translate(prefix + 'other');
	}

	private displayDayOfWeek(dayOfWeek: number): string {
		const index = clamp(dayOfWeek, 0, MAX_DAY_OF_WEEK);
		const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
		return this.ls.translate('calendar.days.' + days[index]);
	}
}
